import React, { useEffect, useState } from 'react';
import Spinner from 'react-bootstrap/Spinner';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css';
import { useDispatch, useSelector } from 'react-redux';
import { fetchLicenses, fetchLicensesV2 } from '../../../actions/licensesAction';
import { createLoadingSelector } from '../../../apis/selectors';
import { FeatureFlagKeys } from '../../../constants/ff.posthog';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import SyncronizeIcon from '../../../images/synchronize.png';
import Styles from '../../../themes/style.module.scss';
import { Table } from '../../commonComponents/Table/Table';
import {
  licensesColumns,
  licensesColumnsV2,
  licensesConditionalRowStyles,
  licensesConditionalRowStylesV2,
} from '../../commonComponents/jobs-column';
import { CustomHeader } from './CustomHeader';
import { handleFilterChange } from '../../commonComponents/Table/utils';

const ROWS_PER_PAGE = 15;

const getDefaultFilterV1 = (searchParams) => ({
  profession: searchParams.profession,
  subProfession: searchParams.subProfession,
  status: searchParams.status,
  state: searchParams.state,
  credentialType: searchParams.credential_type,
  accountStatus: searchParams.account_status,
  govIdUploaded: searchParams.gov_uploaded || searchParams.gov_status,
  expiresSoon: searchParams.expiresSoon,
  contractorStatus: searchParams.contractor_status,
  employeeStatus: searchParams.employee_status,
  page: searchParams.page || 1,
  sortField: searchParams.sortField || 'createdAt',
  sortOrder: searchParams.sortOrder || 'desc',
});

const getDefaultFilterV2 = (searchParams) => ({
  profession: searchParams.profession || '',
  status: searchParams.status || '',
  state: searchParams.state || '',
  credentialType: searchParams.credential_type || '',
  expiresSoon: searchParams.expiresSoon || '',
  page: searchParams.page || 1,
  sortField: searchParams.sortField || 'created_at',
  sortOrder: searchParams.sortOrder || 'desc',
});

const actions = ['GET_ALL_UNVERIFIED_LICENSES'];
const actionsV2 = ['GET_CREDENTIALS_V2'];
const loadingSelector = createLoadingSelector(actions);
const loadingSelectorV2 = createLoadingSelector(actionsV2);

export const Licenses = () => {
  const dispatch = useDispatch();
  const isCredentialingV2 = useFeatureFlag(FeatureFlagKeys.credentialingV2);
  const isLoading = useSelector((state) =>
    isCredentialingV2 ? loadingSelectorV2(state) : loadingSelector(state),
  );
  const licenses = useSelector((state) =>
    isCredentialingV2 ? state.license.credentialsV2 : state.license.licenses,
  );
  const searchParams = useSelector((state) => state.license.searchParams);
  const totalLicenses = useSelector((state) =>
    isCredentialingV2 ? state.license.totalCredentialsV2 : state.license.totalLicenses,
  );
  const totalPages = Math.ceil(totalLicenses / ROWS_PER_PAGE);

  const defaultFilter = isCredentialingV2
    ? getDefaultFilterV2(searchParams)
    : getDefaultFilterV1(searchParams);
  const [filter, setFilter] = useState(defaultFilter);

  useEffect(() => {
    setFilter(isCredentialingV2
      ? getDefaultFilterV2(searchParams)
      : getDefaultFilterV1(searchParams));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCredentialingV2]);

  const resetFilters = () => {
    setFilter(defaultFilter);
  };

  const getUserUrl = (userId, userType) => `/user/${userType.toLowerCase()}/${userId}`;

  const getColumn = () =>
    isCredentialingV2
      ? licensesColumnsV2({ getUserUrl })
      : licensesColumns({ getUserUrl });

  const handleTableChange = (page) => {
    setFilter((prevState) => ({ ...prevState, page }));
  };

  const handleOnSort = (selectedColumn, sortDirection) => {
    const sortObj = {
      page: filter.page,
      sortField: selectedColumn.sortField,
      sortOrder: sortDirection,
    };

    setFilter((prevState) => ({
      ...prevState,
      ...sortObj,
    }));
  };

  useEffect(() => {
    if (isCredentialingV2) {
      dispatch(fetchLicensesV2({ ...filter }));
    } else {
      dispatch(fetchLicenses({ ...filter }));
    }
  }, [dispatch, filter, isCredentialingV2]);

  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        overflowY: 'auto',
        flexDirection: 'column',
        position: 'relative',
        alignItems: 'center',
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'row', marginTop: 20, alignItems: 'center' }}>
          <h3 className={Styles.h3} style={{ fontSize: '24px', textAlign: 'center' }}>
            Licenses
          </h3>
          {isLoading ? (
            <div>
              <Spinner
                animation="border"
                role="status"
                variant="info"
                style={{
                  height: 30,
                  width: 30,
                  marginLeft: 20,
                  marginTop: 10,
                }}
              />
            </div>
          ) : (
            <img
              src={SyncronizeIcon}
              width="38px"
              height="38px"
              style={{
                cursor: 'pointer',
                marginLeft: 8,
              }}
              alt="reload action"
              onClick={resetFilters}
            />
          )}
        </div>
      </div>
      <div style={{ width: '90%', paddingTop: 60 }}>
        <CustomHeader
          filter={filter}
          handleFilterChange={(type, value) => handleFilterChange(type, value, setFilter)}
          isCredentialingV2={isCredentialingV2}
        />
        <Table
          keyField={isCredentialingV2 ? '_rowKey' : 'licenses.id'}
          initialPage={filter.page}
          columns={getColumn()}
          data={licenses}
          noDataComponent="There are no Licenses"
          isLoading={isLoading}
          totalPages={totalPages}
          paginationPerPage={ROWS_PER_PAGE}
          paginationTotalRows={totalLicenses}
          onSort={handleOnSort}
          onPageChange={handleTableChange}
          conditionalRowStyles={
            isCredentialingV2 ? licensesConditionalRowStylesV2 : licensesConditionalRowStyles
          }
        />
      </div>
    </div>
  );
};
